/**
 * useDefaultMapCenter
 *
 * Resolve as coordenadas iniciais do mapa Field Intelligence com base nos
 * campos `uf` e `municipio` do branding do tenant.
 *
 * Prioridade:
 *  1. Município → geocodifica via Nominatim (OSM) — cache 24h
 *  2. UF        → coordenadas estáticas de UF_COORDS
 *  3. Fallback  → Brasil completo
 *
 * O resultado é cacheado no React Query com staleTime de 24h para não
 * sobrecarregar a API pública do Nominatim (respeito ao rate limit de 1 req/s).
 */

import { useQuery } from '@tanstack/react-query';
import { UF_COORDS, BRAZIL_DEFAULT } from '../lib/ufCoords';

interface MapCenter {
  center: [number, number];
  zoom:   number;
}

async function geocodeMunicipio(municipio: string, uf: string): Promise<MapCenter | null> {
  try {
    const query = encodeURIComponent(`${municipio}, ${uf}, Brasil`);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&countrycodes=br`,
      {
        headers: {
          // Nominatim exige User-Agent identificável
          'User-Agent': 'LumeniseBase/1.0 (https://lumenise.com.br)',
          'Accept-Language': 'pt-BR,pt',
        },
      },
    );
    if (!res.ok) return null;
    const data = await res.json() as Array<{ lat: string; lon: string }>;
    if (!data.length) return null;
    return {
      center: [parseFloat(data[0].lon), parseFloat(data[0].lat)],
      zoom:   11,
    };
  } catch {
    return null;
  }
}

export function useDefaultMapCenter(uf?: string | null, municipio?: string | null): MapCenter {
  // Resolução estática de UF (síncrona, sem fetch)
  const ufUpper = uf?.toUpperCase().trim();
  const ufCenter = ufUpper ? UF_COORDS[ufUpper] : undefined;

  // Geocodifica município somente quando os dois campos estão presentes
  const canGeocode = !!municipio && !!ufUpper;

  const { data: geocoded } = useQuery<MapCenter | null>({
    queryKey:  ['map-center-geocode', municipio, ufUpper],
    queryFn:   () => geocodeMunicipio(municipio!, ufUpper!),
    enabled:   canGeocode,
    staleTime: 24 * 60 * 60 * 1000, // 24h — Nominatim rate limit
    retry:     1,
  });

  // Prioridade: geocode município → centro do estado → Brasil
  if (geocoded) return geocoded;

  if (ufCenter) {
    return { center: [ufCenter[0], ufCenter[1]], zoom: ufCenter[2] };
  }

  return { center: [BRAZIL_DEFAULT[0], BRAZIL_DEFAULT[1]], zoom: BRAZIL_DEFAULT[2] };
}
